import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { post } from "../../api";
import Button from "../../components/Button";
import Frame from "../../components/Frame";
import FrameHeader from "../../components/FrameHeader";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import SerialPicker from "../../components/SerialPicker";
import TextArea from "../../components/TextArea";
import { deviceTypeLabel } from "../../constants/sentinel_types";
import { downloadWorkbook } from "../../helpers/excel";
import {
  BATTERY_SOURCE_RECORD,
  EXPORT_COLUMNS,
  MAX_SERIALS,
  RESULT_INVALID,
  RESULT_LABELS,
  RESULT_NOT_FOUND,
  RESULT_READY,
  RESULT_USED,
  activationSummary,
  addSerial,
  assetSummary,
  batteryDisplay,
  batteryTone,
  classifyResult,
  customerSummary,
  exportFileName,
  formatDate,
  formatDateTime,
  formatVolts,
  hologramConnectedSummary,
  licenseSummary,
  removeSerial,
  splitSerials,
  telemetrySummary,
  toExportRows,
} from "../../helpers/preShipment";
import getStatus from "../../helpers/statuses";

import styles from "./AdminPortalPreShipment.module.scss";

const FILTER_ALL = "all";

/** Tile order on the results bar. Counts come straight off the API summary. */
const FILTERS = [
  { key: FILTER_ALL, label: "Checked", count: (s) => s.requested_count },
  { key: RESULT_READY, label: RESULT_LABELS[RESULT_READY], count: (s) => s.unused_count },
  { key: RESULT_USED, label: RESULT_LABELS[RESULT_USED], count: (s) => s.used_count },
  { key: RESULT_NOT_FOUND, label: RESULT_LABELS[RESULT_NOT_FOUND], count: (s) => s.not_found_count },
  { key: RESULT_INVALID, label: RESULT_LABELS[RESULT_INVALID], count: (s) => s.invalid_count },
];

const RESULT_ICONS = {
  [RESULT_READY]: "check_circle",
  [RESULT_USED]: "history",
  [RESULT_NOT_FOUND]: "help_outline",
  [RESULT_INVALID]: "error_outline",
};

const CHECK_COLORS = { fired: "#c86000", clean: "#048b06" };

function pluralise(count, noun) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function classes(...names) {
  return names.filter((c) => c).join(" ");
}

export default function AdminPortalPreShipment() {
  const [text, setText] = useState("");
  const [checking, setChecking] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [exporting, setExporting] = useState(false);

  const entries = useMemo(() => splitSerials(text), [text]);
  const tooMany = entries.length > MAX_SERIALS;

  const visibleRows = useMemo(() => {
    const rows = (summary && summary.results) || [];
    if (filter === FILTER_ALL) return rows;
    return rows.filter((row) => classifyResult(row) === filter);
  }, [summary, filter]);

  async function runCheck(e) {
    e.preventDefault();
    if (entries.length === 0 || tooMany || checking) return;

    setChecking(true);
    setError(null);

    /**
     * The server splits on the same delimiters, so the raw text would do; the
     * joined list just keeps the payload tidy when a whole column is pasted.
     */
    const res = await post("/devices/usage", { serials: entries.join(",") });

    setChecking(false);

    if (res === undefined || !Array.isArray(res.results)) {
      setError("The check could not be completed. Try again.");
      return;
    }

    setSummary(res);
    setFilter(FILTER_ALL);
  }

  function clear() {
    setText("");
    setSummary(null);
    setError(null);
    setFilter(FILTER_ALL);
  }

  /**
   * The picker edits the same text the operator can type into, so the count
   * and the check always run on exactly what is in the box.
   */
  function toggleSerial(serial, add) {
    setText((current) => (add ? addSerial(current, serial) : removeSerial(current, serial)));
  }

  /**
   * Exports what is on screen: with a filter applied that is the filtered
   * list, so "Ready to ship" can go straight to the warehouse as a packing
   * list; with no filter it is the whole check.
   */
  async function exportExcel() {
    if (visibleRows.length === 0 || exporting) return;

    setExporting(true);
    setError(null);

    try {
      await downloadWorkbook({
        fileName: exportFileName(),
        sheetName: "Pre-shipment test",
        columns: EXPORT_COLUMNS,
        rows: toExportRows(visibleRows, { deviceTypeLabel }),
      });
    } catch (e) {
      setError("The spreadsheet could not be created. Try again.");
    }

    setExporting(false);
  }

  /* ── Results: tiles ─────────────────────────────────────────────────── */

  function renderTiles() {
    return (
      <div className={styles.tiles}>
        {FILTERS.map((item) => {
          const count = item.count(summary) || 0;
          const active = filter === item.key;
          const empty = count === 0 && item.key !== FILTER_ALL;

          return (
            <button
              key={item.key}
              type="button"
              className={classes(
                styles.tile,
                styles[`tile_${item.key}`],
                active ? styles.tileActive : "",
                empty ? styles.tileEmpty : ""
              )}
              disabled={empty}
              aria-pressed={active}
              onClick={() => setFilter(item.key)}
            >
              <span className={styles.tileCount}>{count}</span>
              <span className={styles.tileLabel}>
                {item.key !== FILTER_ALL && (
                  <Icon name={RESULT_ICONS[item.key]} size={14} />
                )}
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  /* ── Results: one row per serial ────────────────────────────────────── */

  function renderBadge(result) {
    return (
      <span className={classes(styles.badge, styles[`badge_${result}`])}>
        <Icon name={RESULT_ICONS[result]} size={15} />
        {RESULT_LABELS[result]}
      </span>
    );
  }

  /** First column: the serial as typed, the device's name if it has one, and its flags. */
  function renderSerial(row) {
    if (!row.found) {
      return <div className={styles.serial}>{row.serial}</div>;
    }

    return (
      <>
        <div className={styles.serial}>
          {/* Plain text on purpose: a serial gets copied far more often than clicked. */}
          <span className={styles.serialText}>{row.serial}</span>
          {/* A deleted row has no page to open; the id still tells you which one it was. */}
          {row.device_id != null && !row.device_deleted && (
            <a
              className={styles.openDevice}
              href={`/devices/${row.device_id}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open device in a new window"
              aria-label={`Open device ${row.serial} in a new window`}
            >
              <Icon name="open_in_new" size={15} />
            </a>
          )}
        </div>
        {row.device_name && row.device_name !== row.serial && (
          <div className={styles.deviceName}>{row.device_name}</div>
        )}
        <div className={styles.flags}>
          {row.in_stock && (
            <span className={styles.flag} title="Has never belonged to anyone but Traffix">In stock</span>
          )}
          {!row.device_active && <span className={styles.flag}>Inactive</span>}
          {row.device_deleted && (
            <span className={classes(styles.flag, styles.flagWarn)}>Deleted</span>
          )}
          {row.damaged && (
            <span className={classes(styles.flag, styles.flagWarn)}>Damaged</span>
          )}
          {row.device_ids && row.device_ids.length > 1 && (
            <span className={styles.flag}>
              {pluralise(row.device_ids.length, "record")}
            </span>
          )}
        </div>
      </>
    );
  }

  /** Third column: what the device is, where it is, what it remembers, and the four checks. */
  function renderDetails(row) {
    if (!row.found) {
      return <div className={styles.meta}>{row.error}</div>;
    }

    const status = row.device_status ? getStatus(row.device_status) : null;

    return (
      <>
        <div className={styles.meta}>
          <span className={styles.metaItem}>
            <Icon name="memory" size={14} />
            {deviceTypeLabel(row.devicetype_id) || "Unknown type"}
          </span>
          {status && (
            <span className={styles.metaItem}>
              <span
                className={styles.statusDot}
                style={{ backgroundColor: status.colors[0] }}
              />
              {status.name}
            </span>
          )}
          <span className={styles.metaItem}>
            <Icon name="business" size={14} />
            {row.customer_name || <span className={styles.muted}>No customer</span>}
          </span>
          {row.installed_on_asset && (
            <span className={styles.metaItem}>
              <Icon name="place" size={14} />
              {row.asset_id != null ? (
                <Link to={`/assets/${row.asset_id}`}>{assetSummary(row)}</Link>
              ) : (
                assetSummary(row)
              )}
            </span>
          )}
        </div>

        {/* What the device row remembers - the same numbers the device page shows. */}
        <div className={classes(styles.meta, styles.metaSecondary)}>
          <span className={styles.metaItem}>
            <Icon name="schedule" size={14} />
            {row.status_updated_time
              ? `Last check-in ${formatDateTime(row.status_updated_time)}`
              : "Never checked in"}
          </span>
          {row.signal_percent != null && row.signal_percent > 0 && (
            <span className={styles.metaItem}>
              <Icon name="signal_cellular_alt" size={14} />
              Signal {row.signal_percent}%
            </span>
          )}
          {row.firmware && (
            <span className={styles.metaItem}>
              <Icon name="sd_card" size={14} />
              v{row.firmware}
            </span>
          )}
          {row.iccid && (
            <span className={styles.metaItem} title="SIM ICCID">
              <Icon name="sim_card" size={14} />
              {row.iccid}
            </span>
          )}
        </div>

        {renderHologram(row)}
        {renderChecks(row)}
      </>
    );
  }

  /**
   * Everything the carrier said about the SIM, on one line. Registration is
   * normal for a stock unit (activation claims the SIM), so it is shown as plain
   * information; a data session is what makes the unit used, and that is loud.
   */
  function renderHologram(row) {
    if (!row.hologram_checked) {
      return (
        <div className={classes(styles.meta, styles.hologram)}>
          <span className={styles.metaItem} title={row.hologram_error}>
            <Icon name="cell_tower" size={14} />
            Hologram
          </span>
          <span className={styles.metaItem}>
            <span className={classes(styles.simState, styles.simState_unknown)}>Unknown</span>
            <span className={styles.muted}>{row.hologram_error}</span>
          </span>
        </div>
      );
    }

    if (!row.hologram_registered) {
      return (
        <div className={classes(styles.meta, styles.hologram)}>
          <span className={styles.metaItem}>
            <Icon name="cell_tower" size={14} />
            Hologram
          </span>
          <span className={styles.metaItem}>
            <span className={classes(styles.simState, styles.simState_none)}>Not registered</span>
            <span className={styles.muted}>SIM has never been claimed - it cannot have connected.</span>
          </span>
        </div>
      );
    }

    const state = (row.hologram_state || "").toUpperCase();
    const stateTone = state === "LIVE"
      ? "live"
      : state.indexOf("PAUSED") === 0 || state.indexOf("TEST") === 0
      ? "paused"
      : state === "DEAD" || state === "FROZEN"
      ? "dead"
      : "off";
    const imeiMismatch =
      row.hologram_imei && row.serial && row.hologram_imei.replace(/\D/g, "").indexOf(row.serial.replace(/\D/g, "")) !== 0;
    const megabytes = row.hologram_data_used_bytes != null ? Math.round(row.hologram_data_used_bytes / 10485.76) / 100 : null;

    return (
      <div className={classes(styles.meta, styles.hologram)}>
        <span className={styles.metaItem}>
          <Icon name="cell_tower" size={14} />
          Hologram
        </span>
        <span className={styles.metaItem} title={row.hologram_device_id != null ? `Hologram device ${row.hologram_device_id}` : undefined}>
          <span className={classes(styles.simState, styles[`simState_${stateTone}`])}>{row.hologram_state || "Registered"}</span>
          {row.hologram_claimed_time && `claimed ${formatDate(row.hologram_claimed_time)}`}
        </span>
        {/* Loud only once the unit has left stock; a session on a stock unit is the bench at Traffix. */}
        <span className={classes(styles.metaItem, row.hologram_connected && !row.in_stock ? styles.hologramConnected : "")}>
          <Icon name={row.hologram_connected ? "swap_vert" : "signal_cellular_off"} size={14} />
          {row.hologram_connected
            ? `${row.in_stock ? "Bench session" : "Last session"}${
                row.hologram_last_connect_time ? ` ${formatDateTime(row.hologram_last_connect_time)}` : ""
              }${row.hologram_last_network ? ` via ${row.hologram_last_network}` : ""}`
            : "Never connected"}
        </span>
        {megabytes != null && megabytes > 0 && (
          <span className={styles.metaItem}>
            <Icon name="data_usage" size={14} />
            {megabytes} MB this period
          </span>
        )}
        {imeiMismatch && (
          <span className={classes(styles.metaItem, styles.hologramWarn)} title="Hologram last saw this SIM in a different unit">
            <Icon name="swap_horiz" size={14} />
            SIM last seen in {row.hologram_imei}
          </span>
        )}
      </div>
    );
  }

  /**
   * The four ownership marks as pills - a mark that fired is the reason the
   * device is not shipping, so it is the loud one; a clean mark is quiet -
   * followed by two quieter facts, telemetry and carrier sessions, that say the
   * unit was powered on but never decide anything.
   */
  function renderChecks(row) {
    const pill = (fired, label, detail, title) => (
      <span
        className={classes(styles.pill, fired ? styles.pillFired : styles.pillClean)}
        title={title}
      >
        <Icon
          name={fired ? "warning" : "check"}
          size={14}
          color={fired ? CHECK_COLORS.fired : CHECK_COLORS.clean}
        />
        <span className={styles.pillLabel}>{label}</span>
        <span className={styles.pillDetail}>{detail}</span>
      </span>
    );

    /* Reported, never judged: being switched on is not ownership. */
    const info = (label, detail, title) => (
      <span className={classes(styles.pill, styles.pillInfo)} title={title}>
        <Icon name="info_outline" size={14} color="#a9a9a9" />
        <span className={styles.pillLabel}>{label}</span>
        <span className={styles.pillDetail}>{detail}</span>
      </span>
    );

    /* The carrier could not be asked, and it says why on hover. */
    const unknown = (label) => (
      <span className={classes(styles.pill, styles.pillUnknown)} title={row.hologram_error}>
        <Icon name="help_outline" size={14} color="#a9a9a9" />
        <span className={styles.pillLabel}>{label}</span>
        <span className={styles.pillDetail}>Unknown</span>
      </span>
    );

    return (
      <div className={styles.pills}>
        {pill(row.has_license, "License", licenseSummary(row))}
        {pill(row.ever_installed_on_asset, "Asset", assetSummary(row))}
        {pill(row.activated, "Activated", activationSummary(row))}
        {pill(
          !row.in_stock,
          "Customer",
          customerSummary(row),
          row.in_stock ? "Has never belonged to anyone but Traffix" : "Has belonged to a customer - no longer Traffix stock"
        )}
        {info(
          "Telemetry",
          telemetrySummary(row),
          row.bench_telemetry ? "Received while the device was still stock at Traffix - bench testing" : undefined
        )}
        {row.hologram_checked
          ? info("Network", hologramConnectedSummary(row), row.hologram_last_network ? `Last network: ${row.hologram_last_network}` : undefined)
          : unknown("Network")}
      </div>
    );
  }

  /**
   * A measured level (from telemetry) is shown in full colour with its voltage
   * and date. When telemetry is gone but the device has been in service, the
   * level remembered on the device row stands in, clearly marked as last known.
   */
  function renderBattery(row) {
    if (!row.found) return null;

    const battery = batteryDisplay(row);
    if (!battery) {
      return (
        <div className={styles.batteryNone}>
          <Icon name="battery_unknown" size={22} color="#b8b8b8" />
          <span>No reading</span>
        </div>
      );
    }

    const tone = batteryTone(battery.percent);
    const fromRecord = battery.source === BATTERY_SOURCE_RECORD;

    return (
      <div className={classes(styles.battery, fromRecord ? styles.batteryRecord : "")}>
        <div className={classes(styles.batteryPercent, styles[`batteryText_${tone}`])}>
          {battery.percent}
          <span className={styles.batteryUnit}>%</span>
        </div>
        <div className={styles.batteryBar}>
          <div
            className={classes(styles.batteryFill, styles[`batteryFill_${tone}`])}
            style={{ width: `${battery.percent}%` }}
          />
        </div>
        <div className={styles.batteryMeta}>
          {fromRecord ? "Last known" : formatVolts(battery.reading)}
          {battery.time && ` · ${formatDate(battery.time)}`}
        </div>
      </div>
    );
  }

  function renderRow(row) {
    const result = classifyResult(row);

    return (
      <div key={row.serial} className={classes(styles.row, styles[`row_${result}`])}>
        <div className={styles.rowSerial}>{renderSerial(row)}</div>
        <div className={styles.rowResult}>{renderBadge(result)}</div>
        <div className={styles.rowMain}>{renderDetails(row)}</div>
        <div className={styles.rowBattery}>{renderBattery(row)}</div>
      </div>
    );
  }

  /**
   * Shown in place of the results while a check is running. Stale rows from
   * the previous check are hidden rather than dimmed, so nobody reads an old
   * verdict against a new list of serials.
   */
  function renderChecking() {
    return (
      <>
        <div className={styles.resultsHeader}>
          <FrameHeader title="Results" />
        </div>
        <Frame>
          <div className={styles.checking}>
            <Loading color="gray" size={40} />
            <div className={styles.checkingText}>
              Checking {pluralise(entries.length, "serial number")}…
            </div>
            <div className={styles.checkingHint}>
              Looking up telemetry, licenses, asset installations, activations and the SIM at Hologram.
            </div>
          </div>
        </Frame>
      </>
    );
  }

  function renderResults() {
    return (
      <>
        <div className={styles.resultsHeader}>
          <FrameHeader title="Results">
            <div className={styles.resultsActions}>
              <Button
                icon="file_download"
                disabled={visibleRows.length === 0 || exporting}
                isLoading={exporting}
                onClick={exportExcel}
              >
                Export to Excel
              </Button>
            </div>
          </FrameHeader>
        </div>

        <Frame padding={0}>
          {renderTiles()}

          {visibleRows.length === 0 ? (
            <div className={styles.empty}>Nothing to show for this filter.</div>
          ) : (
            <>
              <div className={styles.listHeader}>
                <span>Serial</span>
                <span>Result</span>
                <span>Device</span>
                <span className={styles.listHeaderBattery}>Battery</span>
              </div>
              <div className={styles.rows}>{visibleRows.map(renderRow)}</div>
            </>
          )}
        </Frame>
      </>
    );
  }

  /* ── Page ───────────────────────────────────────────────────────────── */

  return (
    <>
      <FrameHeader title="Device pre-shipment test" />

      <Frame>
        <form onSubmit={runCheck}>
          <p className={styles.intro}>
            Checks whether each device has ever been used and reads its last battery
            level. Only marks of
            ownership count: a licence, an asset it was mounted on, an activation, or
            a customer other than Traffix. Being powered on at Traffix - telemetry or
            a network session - is bench testing and is not held against it.
          </p>

          <div className={styles.pickerRow}>
            <SerialPicker selected={entries} onToggle={toggleSerial} disabled={checking} />
          </div>

          <TextArea
            value={text}
            placeholder="Paste serial numbers - one per line, or separated by commas, semicolons or spaces - or pick them above"
            onChange={(e) => setText(e.target.value)}
            disabled={checking}
            rows={5}
            spellCheck={false}
          />

          <div className={styles.inputBar}>
            <div className={classes(styles.count, tooMany ? styles.countOver : "")}>
              <Icon name={tooMany ? "error_outline" : "format_list_numbered"} size={16} />
              {entries.length === 0
                ? "No serial numbers yet"
                : pluralise(entries.length, "serial number")}
              {tooMany && ` - at most ${MAX_SERIALS} per check`}
            </div>
            <div className={styles.actions}>
              {(text || summary) && (
                <Button onClick={clear} disabled={checking} type="button">
                  Clear
                </Button>
              )}
              <Button
                theme="primary"
                type="submit"
                disabled={entries.length === 0 || tooMany || checking}
                isLoading={checking}
              >
                Check devices
              </Button>
            </div>
          </div>
        </form>
      </Frame>

      {error && <div className={styles.error}>{error}</div>}

      {checking ? renderChecking() : summary && renderResults()}
    </>
  );
}
